import React, { useEffect, useMemo, useState } from "react";
import { Repeat, Search, Shield, RotateCcw, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState, Modal } from "@/components/common";

const TYPES = [
  { key: "", label: "Semua" }, { key: "inbound", label: "Stock In" }, { key: "consume_reserved", label: "Sales/Out" },
  { key: "production", label: "Produksi" }, { key: "spoilage", label: "Waste" }, { key: "transfer_out", label: "Transfer" },
  { key: "adjustment", label: "Adjustment" }, { key: "opname", label: "Opname" }, { key: "reversal", label: "Reversal" },
];

export default function StockMovements() {
  const { branch, can } = useAuth();
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [reversing, setReversing] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => { if (!branch) return; setRows(null); api.get("/inventory/movements", { branch_id: branch.id, movement_type: type || undefined, limit: 500 }).then(setRows).catch(() => setRows([])); };
  useEffect(load, [branch, type]);
  const filtered = useMemo(() => (rows || []).filter((r) => `${r.item_name} ${r.user_name} ${r.note} ${r.source_id || ""}`.toLowerCase().includes(search.toLowerCase())), [rows, search]);

  const submitReverse = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/inventory/movements/${reversing.id}/reverse`, { reason }); toast.success("Reversal dibuat"); setReversing(null); setReason(""); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Stock Movement" subtitle="Ledger permanen · tanpa DELETE" icon={Repeat} />
      <div className="relative max-w-sm mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari item, user, sumber transaksi…" data-testid="movement-search" className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
        {TYPES.map((t) => <button key={t.key || "all"} onClick={() => setType(t.key)} data-testid={`movement-filter-${t.key || "all"}`} className={`px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap ${type === t.key ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{t.label}</button>)}
      </div>

      {rows === null ? <Loading /> : (
        <>
          <Card className="p-4 mb-4 bg-primary/5 border-primary/20" data-testid="movement-immutable-notice">
            <div className="flex gap-2 text-sm text-slate-600"><Shield size={17} className="text-primary shrink-0" /><span>Histori tidak dapat dihapus. Koreksi membuat transaksi REVERSAL baru sehingga jejak sebelum & sesudah tetap utuh.</span></div>
          </Card>
          {filtered.length === 0 && <EmptyState icon={Repeat} title="Belum ada pergerakan" message="Tidak ada pergerakan stok untuk filter ini." />}
          <div className="space-y-2">
            {filtered.map((m) => {
              const positive = Number(m.delta_physical) >= 0;
              return (
                <Card key={m.id} className="p-4" data-testid={`movement-row-${m.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0"><div className="font-semibold text-slate-800">{m.item_name}</div><div className="text-xs text-slate-400 mt-0.5">{fmtDate(m.created_at)} · {m.user_name || "Sistem"}</div></div>
                    <Badge className={m.type === "reversal" ? "bg-amber-100 text-amber-700" : positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{m.type === "reversal" ? "REVERSAL" : m.reason || m.type}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="bg-slate-50 rounded-lg px-3 py-1.5"><div className="text-[10px] text-slate-400">Sebelum</div><div className="font-bold text-slate-800">{num(m.balance_before)}</div></div>
                    <ArrowRight size={16} className="text-slate-400" />
                    <div className="bg-slate-50 rounded-lg px-3 py-1.5"><div className="text-[10px] text-slate-400">Sesudah</div><div className="font-bold text-slate-800">{num(m.balance_after)}</div></div>
                    <span className={`ml-auto text-lg font-bold ${positive ? "text-emerald-600" : "text-rose-600"}`}>{positive ? "+" : ""}{num(m.delta_physical)}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">Sumber: {m.source_type || m.type}{m.source_id ? ` · ${m.source_id}` : " · transaksi manual"}</div>
                  {m.note && <div className="text-xs text-slate-500 mt-1">{m.note}</div>}
                  {m.reversed_by_movement_id && <div className="text-xs text-amber-600 font-semibold mt-1">Sudah direversal: {m.reversed_by_movement_id}</div>}
                  {can("inventory", "approve") && m.can_reverse && <button onClick={() => { setReversing(m); setReason(""); }} data-testid={`movement-reverse-${m.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary font-medium"><RotateCcw size={14} /> Buat Reversal</button>}
                </Card>
              );
            })}
          </div>
        </>
      )}

      {reversing && (
        <Modal open onClose={() => setReversing(null)} title="Buat reversal pergerakan stok" maxWidth="max-w-md">
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{reversing.item_name} · {num(reversing.delta_physical)}</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan reversal (wajib)…" rows={3} data-testid="movement-reversal-reason" className="w-full p-3 rounded-xl border border-slate-300 outline-none focus:border-primary text-sm" />
            <Button className="w-full" onClick={submitReverse} data-testid="movement-reversal-submit">Buat Reversal</Button>
          </div>
        </Modal>
      )}
    </>
  );
}
